/**
 * Locale Storage Utilities
 *
 * Handles persisting and retrieving locale preference from localStorage
 */

import { type Locale, DEFAULT_LOCALE, SUPPORTED_LOCALES } from './types';

/**
 * localStorage key for storing locale preference
 */
const LOCALE_STORAGE_KEY = 'erp-locale';

/**
 * Check if localStorage is available
 */
function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__locale_test__';
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Get the stored locale from localStorage
 * Returns the default locale if not set or invalid
 */
export function getStoredLocale(): Locale {
  if (!isLocalStorageAvailable()) {
    return DEFAULT_LOCALE;
  }

  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);

    if (!stored) {
      return DEFAULT_LOCALE;
    }

    // Validate that stored value is a supported locale
    if (SUPPORTED_LOCALES.includes(stored as Locale)) {
      return stored as Locale;
    }

    // Invalid value, return default
    return DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
}

/**
 * Store the locale preference in localStorage
 */
export function storeLocale(locale: Locale): void {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    // Validate locale before storing
    if (!SUPPORTED_LOCALES.includes(locale)) {
      console.warn(`Attempted to store unsupported locale: ${locale}`);
      return;
    }

    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch (error) {
    console.error('Failed to store locale preference:', error);
  }
}

/**
 * Remove the stored locale preference from localStorage
 */
export function clearStoredLocale(): void {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    window.localStorage.removeItem(LOCALE_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear locale preference:', error);
  }
}

/**
 * Get browser's preferred language
 * Returns default locale if browser language is not supported
 */
export function getBrowserLocale(): Locale {
  const browserLang = navigator.language || (navigator as { userLanguage?: string }).userLanguage;

  if (!browserLang) {
    return DEFAULT_LOCALE;
  }

  // Check for exact match (e.g., 'zh-CN', 'en-US')
  if (SUPPORTED_LOCALES.includes(browserLang as Locale)) {
    return browserLang as Locale;
  }

  // Check for language prefix match (e.g., 'zh' -> 'zh-CN', 'en' -> 'en-US')
  const langPrefix = browserLang.split('-')[0];
  if (langPrefix === 'zh') {
    return 'zh-CN';
  }
  if (langPrefix === 'en') {
    return 'en-US';
  }

  return DEFAULT_LOCALE;
}

/**
 * Get the effective locale to use
 * Priority: stored preference > browser language > default
 */
export function getEffectiveLocale(): Locale {
  const stored = getStoredLocale();

  // If user has explicitly set a preference, use it
  if (stored !== DEFAULT_LOCALE || localStorage.getItem(LOCALE_STORAGE_KEY)) {
    return stored;
  }

  // Otherwise, try to detect from browser
  return getBrowserLocale();
}
